// ============================================================================
// Typography Components
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Description: Base typography components for consistent text styling
// ============================================================================

import { forwardRef } from 'react';
import type { HTMLAttributes, ElementType, ReactNode } from 'react';
import { clsx } from 'clsx';

/**
 * Heading levels
 */
export type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/**
 * Text variants
 */
export type TextVariant = 'body1' | 'body2' | 'caption' | 'overline' | 'code' | 'muted';

/**
 * Text colors
 */
export type TextColor = 'default' | 'muted' | 'primary' | 'success' | 'warning' | 'error';

/**
 * Base text props
 */
export interface BaseTextProps {
  color?: TextColor;
  className?: string | undefined;
  children?: ReactNode;
}

/**
 * Color styles
 */
const colorStyles: Record<TextColor, string> = {
  default: 'text-gray-900 dark:text-white',
  muted: 'text-gray-500 dark:text-gray-400',
  primary: 'text-blue-600 dark:text-blue-400',
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  error: 'text-red-600 dark:text-red-400',
};

/**
 * Heading Component
 */
export interface HeadingProps
  extends Omit<HTMLAttributes<HTMLHeadingElement>, 'color'>,
    BaseTextProps {
  level?: HeadingLevel;
  as?: HeadingLevel;
}

const headingStyles: Record<HeadingLevel, string> = {
  h1: 'text-4xl font-bold tracking-tight',
  h2: 'text-3xl font-bold tracking-tight',
  h3: 'text-2xl font-semibold',
  h4: 'text-xl font-semibold',
  h5: 'text-lg font-semibold',
  h6: 'text-base font-semibold',
};

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level = 'h2', as, color = 'default', className, children, ...props }, ref) => {
    const Component = as || level;
    const visualLevel = level;

    return (
      <Component
        ref={ref}
        className={clsx(headingStyles[visualLevel], colorStyles[color], className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Heading.displayName = 'Heading';

/**
 * Text Component
 */
export interface TextProps
  extends Omit<HTMLAttributes<HTMLParagraphElement>, 'color'>,
    BaseTextProps {
  variant?: TextVariant;
  as?: ElementType;
  isTruncated?: boolean;
  isMonospace?: boolean;
}

const textVariantStyles: Record<TextVariant, string> = {
  body1: 'text-base leading-relaxed',
  body2: 'text-sm leading-relaxed',
  caption: 'text-xs leading-normal',
  overline: 'text-xs font-medium uppercase tracking-wider',
  code: 'text-sm font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded',
  muted: 'text-sm text-gray-500 dark:text-gray-400',
};

export const Text = forwardRef<HTMLParagraphElement, TextProps>(
  (
    {
      variant = 'body1',
      as = 'p',
      color = 'default',
      isTruncated = false,
      isMonospace = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const Component = as;

    return (
      <Component
        ref={ref}
        className={clsx(
          textVariantStyles[variant],
          colorStyles[color],
          isTruncated && 'truncate',
          isMonospace && 'font-mono',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Text.displayName = 'Text';

/**
 * Label Component
 */
export interface LabelProps
  extends Omit<HTMLAttributes<HTMLLabelElement>, 'color'>,
    BaseTextProps {
  htmlFor?: string;
  isRequired?: boolean;
  required?: boolean; // Alias for isRequired
}

export const Label = forwardRef<HTMLLabelElement, LabelProps>(
  ({ color = 'default', isRequired = false, required, className, children, ...props }, ref) => {
    const showRequired = isRequired || required;
    return (
      <label
        ref={ref}
        className={clsx(
          'text-sm font-medium',
          colorStyles[color],
          className
        )}
        {...props}
      >
        {children}
        {showRequired && <span className="text-red-500 ml-1">*</span>}
      </label>
    );
  }
);

Label.displayName = 'Label';

/**
 * Link Component
 */
export interface LinkProps
  extends Omit<HTMLAttributes<HTMLAnchorElement>, 'color'>,
    BaseTextProps {
  href?: string;
  isExternal?: boolean;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, isExternal = false, color = 'primary', className, children, ...props }, ref) => {
    const externalProps = isExternal
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};

    return (
      <a
        ref={ref}
        href={href}
        className={clsx(
          'hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded',
          color === 'primary'
            ? 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
            : colorStyles[color],
          className
        )}
        {...externalProps}
        {...props}
      >
        {children}
        {isExternal && (
          <svg
            className="inline-block ml-1 h-3 w-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        )}
      </a>
    );
  }
);

Link.displayName = 'Link';

/**
 * Code Block Component
 */
export interface CodeBlockProps extends HTMLAttributes<HTMLPreElement> {
  language?: string;
  children: string;
}

export const CodeBlock = forwardRef<HTMLPreElement, CodeBlockProps>(
  ({ language, className, children, ...props }, ref) => {
    return (
      <pre
        ref={ref}
        className={clsx(
          'bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm font-mono',
          className
        )}
        {...props}
      >
        {language && (
          <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">
            {language}
          </div>
        )}
        <code>{children}</code>
      </pre>
    );
  }
);

CodeBlock.displayName = 'CodeBlock';

export default {
  Heading,
  Text,
  Label,
  Link,
  CodeBlock,
};
